import {
    IGetLandingPage,
    ILandingPageModule,
    ILandingPageProps,
    ISubpage,
    ITranslationKeys,
} from '@interfaces';
import { IRequest, IRequestHarbourSearch, IRequestUGC } from '@interfaces/request';
import { fetchUGC } from '../../../api/resources';
import { formatDateToNamedMonthAndYear } from '../../../utils/date';
import { log } from '../../../utils/logger';
import { __ } from '../../../utils/translation-service';
import { IUGCProps } from './ugc';

interface IGetUGCSubPageProps {
    modulesData: ILandingPageProps['pageProps']['modulesData'];
    getLandingPage: IGetLandingPage;
    slug: string;
    ugcSlug: string;
    component?: ILandingPageModule;
    subpage?: ISubpage;
}

export const getUGCRequest = (args: {
    request: IRequest;
    component?: ILandingPageModule;
}): IRequest => {
    const { request } = args;
    const ugc: IRequestUGC = {
        name: 'ugcHeader',
        includeFilters: {
            id: request.id,
            type: request.mainType,
        },
        sortBy: 'quote',
        limit: 3,
        fields: [
            {
                name: 'aggregation',
                fields: [
                    {
                        name: 'data',
                        fields: [
                            {
                                name: 'attributes',
                                fields: ['overall', 'reviewClass', 'numRatings'],
                            },
                        ],
                    },
                ],
            },
            {
                name: 'rating',
                fields: [
                    {
                        name: 'data',
                        fields: [
                            {
                                name: 'attributes',
                                fields: [
                                    'positivePointsComment',
                                    'name',
                                    'departure',
                                    'ship',
                                    'cruise',
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    };

    request.translations = [
        ...request.translations,
        'ugcExcellent',
        'ugcAverage',
        'ugcOk',
        'ugcGood',
        'ugcVeryGood',
        'ugcUSPHeadline',
        'ugcHeadline',
        'ugcSubpageButton',
        'ugcUSP1',
        'ugcUSP2',
        'ugcUSP3',
        'additionalReviewInfo',
    ];

    request.ugc.push(ugc);

    return request;
};

export const getUGCSubpageRequest = (args: {
    request: IRequest;
    component?: ILandingPageModule;
}): IRequest => {
    const { request } = args;

    const requestNew = getUGCRequest(args);

    const ugc: IRequestUGC = {
        name: 'ugcSubpage',
        includeFilters: {
            id: request.id,
            type: request.mainType,
        },
        sortBy: 'date_desc',
        offset: 0,
        limit: 10,
        fields: [
            {
                name: 'aggregation',
                fields: [
                    {
                        name: 'data',
                        fields: [
                            {
                                name: 'attributes',
                                fields: [
                                    'overall',
                                    'reviewClass',
                                    'numRatings',

                                    'shipRating',
                                    'cabinRating',
                                    'gastronomyRating',
                                    'excursionsRating',
                                    'serviceRating',
                                    'sportRating',
                                    'entertainmentRating',

                                    {
                                        name: 'reviewClasses',
                                        fields: ['reviewClass', 'count', 'rating', 'suffix'],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                name: 'rating',
                fields: [
                    {
                        name: 'data',
                        fields: [
                            {
                                name: 'attributes',
                                fields: [
                                    'name',
                                    'constellation',
                                    'positivePointsComment',
                                    'negativePointsComment',
                                    'cabinComment',
                                    'atmosphere',
                                    'overallRating',
                                    'cruise',
                                    'arrival',
                                    'ageRange',
                                    'cabinType',
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    };

    requestNew.ugc.push(ugc);

    if (request.mainType === 'destination') {
        const topHarboursQuery: IRequestHarbourSearch = {
            name: 'topHarbours',
            fields: [
                {
                    name: 'items',
                    fields: ['title', 'rating'],
                },
            ],
            includeFilters: {
                regionIds: [String(request.id)],
            },
            limit: 10,
            sortBy: 'relevance',
        };
        requestNew.harbourSearches.push(topHarboursQuery);
    }

    requestNew.translations = [
        ...requestNew.translations,
        'ugcShip',
        'ugcCabin',
        'ugcGastronomy',
        'ugcExcursions',
        'ugcService',
        'ugcSport',
        'ugcEntertainment',
        'atmosphereboring',
        'atmospherechaotic',
        'atmospherediverse',
        'atmospherefamiliar',
        'atmosphereelegant',
        'atmosphererelaxed',
        'atmosphereloud',
        'constellationcoupleLabel',
        'constellationfamilyLabel',
        'constellationfamilysmallchildrenLabel',
        'constellationfriendsLabel',
        'constellationsingleLabel',
        'loadMore',
        'additionalReviewInfo',
    ];

    return requestNew;
};

const getMoreUGCReviewsByShipSlug = (shipSlug: string) => {
    return (offset: number, itemsToFetch: number) =>
        fetchUGC({
            slug: shipSlug,
            offset,
            limit: itemsToFetch,
        }).then((data) => data.rating?.data);
};

export const getUGCPropsFromData = ({
    modulesData,
    getLandingPage,
    slug: shipSlug,
    ugcSlug,
    subpage,
}: IGetUGCSubPageProps): IUGCProps | undefined => {
    const ugcHeader = modulesData[`ugcData`];
    if (!ugcHeader || !ugcHeader.aggregation || !ugcHeader.rating) {
        log.warn('getUGCProps called without data');
        return undefined;
    }
    const { overall, reviewClass, numRatings } = ugcHeader.aggregation.data.attributes;

    const review = reviewClass ? reviewClass.replace(' ', '') : '';
    const testimonials = ugcHeader.rating.data
        .map((item: any) => ({
            quote: item.attributes.positivePointsComment,
            name: item.attributes.name,
            additionalInfo: getLandingPage.title,
            time: item.attributes.departure
                ? formatDateToNamedMonthAndYear(item.attributes.departure.split('T')[0])
                : '',
        }))
        .slice(0, 3);

    const ratingString = __(('ugc' + review) as ITranslationKeys);

    let subpageProps = {};

    if (numRatings >= 5 && !subpage) {
        subpageProps = {
            href: `${getLandingPage.url}/${ugcSlug}`,
            buttonText: __('ugcSubpageButton'),
        };
    } else if (subpage) {
        const ugcData = modulesData[`ugcData`];
        if (!ugcData || !ugcData.aggregation || !ugcData.rating) {
            throw new Error('getUGCProps for subpage called without data ');
        }
        const {
            reviewClasses,
            shipRating,
            cabinRating,
            gastronomyRating,
            excursionsRating,
            serviceRating,
            sportRating,
            entertainmentRating,
        } = ugcData.aggregation.data.attributes;
        const ratingRanges = reviewClasses
            .map((item: { reviewClass: string; count: number; rating: number; suffix: string }) => {
                return {
                    value: item.count,
                    description:
                        __(('ugc' + item.reviewClass.replace(' ', '')) as ITranslationKeys) +
                        ' ' +
                        item.suffix,
                };
            })
            .reverse();

        const detailRatings =
            getLandingPage.type === 'destination' && modulesData['harbourSearchtopHarbours']
                ? // For destination we want to show the harbours and not the ship/cabin et al. rating
                  (modulesData['harbourSearchtopHarbours'] as any).items
                      .filter((item: { title: string; rating: number }) => !!item.rating)
                      .map((item: { title: string; rating: number }) => ({
                          value: item.rating.toFixed(1),
                          description: item.title,
                      }))
                : [
                      { value: shipRating, description: __('ugcShip') },
                      { value: cabinRating, description: __('ugcCabin') },
                      { value: gastronomyRating, description: __('ugcGastronomy') },
                      { value: excursionsRating, description: __('ugcExcursions') },
                      { value: serviceRating, description: __('ugcService') },
                      { value: sportRating, description: __('ugcSport') },
                      {
                          value: entertainmentRating,
                          description: __('ugcEntertainment'),
                      },
                  ].map((item) => ({
                      ...item,
                      value:
                          item.value && typeof item.value === 'number'
                              ? item.value.toFixed(1)
                              : item.value || null,
                  }));

        const reviews = ugcData.rating.data;

        subpageProps = {
            ratingRanges,
            detailRatings,
            reviews,
            onShowMore: getMoreUGCReviewsByShipSlug(shipSlug),
        };
    }

    return {
        ...subpageProps,
        headline: `${__('ugcHeadline')}`,
        title: `${getLandingPage.title}`,
        totalRatings: numRatings,
        ratingValue: overall ? Math.round(overall * 10) / 10 : undefined,
        ratingString,
        testimonials,
        requestType: getLandingPage.type,
    };
};
